import { z } from 'zod';
import type { ScenarioDelta, NodeId } from './ScenarioDelta';

export type ScenarioLifecycleState =
  | 'VIEWING_LIVE'
  | 'VIEWING_SCENARIO'
  | 'EDITING_SCENARIO'
  | 'COMMITTING'
  | 'TRANSITIONING';

export const LIFECYCLE_TRANSITIONS: Record<ScenarioLifecycleState, ScenarioLifecycleState[]> = {
  VIEWING_LIVE: ['VIEWING_SCENARIO', 'TRANSITIONING'],
  VIEWING_SCENARIO: ['VIEWING_LIVE', 'EDITING_SCENARIO', 'TRANSITIONING'],
  EDITING_SCENARIO: ['VIEWING_SCENARIO', 'COMMITTING'],
  COMMITTING: ['VIEWING_LIVE', 'VIEWING_SCENARIO'],
  TRANSITIONING: ['VIEWING_LIVE', 'VIEWING_SCENARIO'],
};

export const canTransition = (
  from: ScenarioLifecycleState,
  to: ScenarioLifecycleState
): boolean => LIFECYCLE_TRANSITIONS[from].includes(to);

export interface ScenarioStatusIndex {
  added: Set<NodeId>;
  removed: Set<NodeId>;
  moved: Set<NodeId>;
  modified: Set<NodeId>;
}

export type NodeScenarioStatus = 'added' | 'removed' | 'moved' | 'modified' | 'unchanged';

export const buildStatusIndex = (delta: ScenarioDelta): ScenarioStatusIndex => ({
  added: new Set(delta.additions.map((a) => a.id)),
  removed: new Set(delta.removals.map((r) => r.nodeId)),
  moved: new Set(delta.moves.map((m) => m.nodeId)),
  modified: new Set(delta.updates.map((u) => u.nodeId)),
});

export const getNodeScenarioStatus = (
  nodeId: NodeId,
  index: ScenarioStatusIndex | null
): NodeScenarioStatus => {
  if (!index) return 'unchanged';
  if (index.added.has(nodeId)) return 'added';
  if (index.removed.has(nodeId)) return 'removed';
  if (index.moved.has(nodeId)) return 'moved';
  if (index.modified.has(nodeId)) return 'modified';
  return 'unchanged';
};

export type ActionResult<T = void> =
  | { success: true; data: T }
  | { success: false; error: string };

export const success = <T>(data: T): ActionResult<T> => ({ success: true, data });
export const successVoid = (): ActionResult<void> => ({ success: true, data: undefined });
export const failure = <T = void>(error: string): ActionResult<T> => ({ success: false, error });

export const CreateScenarioSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
});

export type CreateScenarioInput = z.infer<typeof CreateScenarioSchema>;
